// Native Google / Apple sign-in (Capgo social-login plugin). The app only
// collects the provider ID token; the backend (POST /auth/social) verifies
// it and decides which account it belongs to.
//
// Availability:
// - Apple: iOS only (native sheet; Android would need a web redirect flow).
// - Google: iOS needs the iOS + web OAuth client ids, Android the web one
//   (the Android OAuth clients only register package + signing SHA-1 in the
//   Google console). Without them the buttons stay hidden.
// - Web: not offered.
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";

export type SocialProvider = "google" | "apple";

export interface SocialCredential {
  provider: SocialProvider;
  idToken: string;
  /** Apple: exchanged by the backend for the grant it revokes on account deletion. */
  authorizationCode?: string;
  /** Apple sends the name only on the very first sign-in, and only to the app. */
  givenName?: string;
  familyName?: string;
}

const googleWebClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID?.trim() || undefined;
const googleIosClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID?.trim() || undefined;

export function isSocialProviderAvailable(provider: SocialProvider): boolean {
  const platform = Capacitor.getPlatform();
  if (provider === "apple") return platform === "ios";
  if (platform === "ios") return !!googleIosClientId && !!googleWebClientId;
  if (platform === "android") return !!googleWebClientId;
  return false;
}

let initialized: Promise<void> | null = null;

function ensureInitialized(): Promise<void> {
  if (!initialized) {
    const isIos = Capacitor.getPlatform() === "ios";
    initialized = SocialLogin.initialize({
      ...(googleWebClientId
        ? {
            google: {
              webClientId: googleWebClientId,
              iOSClientId: googleIosClientId,
              iOSServerClientId: googleWebClientId,
              mode: "online" as const,
            },
          }
        : {}),
      ...(isIos ? { apple: {} } : {}),
    }).catch((err: unknown) => {
      initialized = null;
      throw err;
    });
  }
  return initialized;
}

function isCancellation(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return /cancel/i.test(message) || /1001/.test(message);
}

/** @returns the credential, or null when the user closed the provider sheet */
export async function signInWithProvider(provider: SocialProvider): Promise<SocialCredential | null> {
  await ensureInitialized();
  try {
    if (provider === "google") {
      const res = await SocialLogin.login({
        provider: "google",
        options: { scopes: ["email", "profile"] },
      });
      const result = res.result;
      if (result.responseType !== "online" || !result.idToken) {
        throw new Error("Google did not return an ID token");
      }
      return { provider: "google", idToken: result.idToken };
    }

    const res = await SocialLogin.login({
      provider: "apple",
      options: { scopes: ["email", "name"] },
    });
    const result = res.result;
    if (!result.idToken) {
      throw new Error("Apple did not return an identity token");
    }
    return {
      provider: "apple",
      idToken: result.idToken,
      // Without token exchange in the plugin the code comes as the access token.
      authorizationCode: result.authorizationCode ?? result.accessToken?.token ?? undefined,
      givenName: result.profile?.givenName ?? undefined,
      familyName: result.profile?.familyName ?? undefined,
    };
  } catch (err) {
    if (isCancellation(err)) return null;
    throw err;
  }
}
